/*
 * Utility helpers to keep CSS viewport variables in sync with the real
 * visual viewport. Mobile browsers shrink the layout viewport when the
 * virtual keyboard appears, which can push overlays outside of the screen.
 *
 * We expose a single initializer that sets CSS custom properties we can use
 * from CSS/Tailwind and from inline styles:
 *   --app-dvh / --app-dvw: dynamic viewport height/width in pixels
 *   --app-viewport-offset-top / left: visual viewport offsets
 *   --app-viewport-scale: current zoom level
 */

let initialized = false

const CSS_VARIABLES = {
  HEIGHT: "--app-dvh",
  WIDTH: "--app-dvw",
  OFFSET_TOP: "--app-viewport-offset-top",
  OFFSET_LEFT: "--app-viewport-offset-left",
  SCALE: "--app-viewport-scale",
} as const

type VisualViewportLike = Pick<
  VisualViewport,
  "height" | "width" | "offsetTop" | "offsetLeft" | "scale"
>

const getViewportMetrics = (): VisualViewportLike => {
  if (typeof window === "undefined") {
    return {
      height: 0,
      width: 0,
      offsetTop: 0,
      offsetLeft: 0,
      scale: 1,
    }
  }

  const viewport = window.visualViewport
  if (viewport) {
    return {
      height: viewport.height,
      width: viewport.width,
      offsetTop: viewport.offsetTop,
      offsetLeft: viewport.offsetLeft,
      scale: viewport.scale,
    }
  }

  return {
    height: window.innerHeight,
    width: window.innerWidth,
    offsetTop: 0,
    offsetLeft: 0,
    scale: 1,
  }
}

const setCSSVariables = (metrics: VisualViewportLike) => {
  if (typeof document === "undefined") return
  const root = document.documentElement
  root.style.setProperty(CSS_VARIABLES.HEIGHT, `${Math.round(metrics.height)}px`)
  root.style.setProperty(CSS_VARIABLES.WIDTH, `${Math.round(metrics.width)}px`)
  root.style.setProperty(
    CSS_VARIABLES.OFFSET_TOP,
    `${Math.max(0, metrics.offsetTop)}px`
  )
  root.style.setProperty(
    CSS_VARIABLES.OFFSET_LEFT,
    `${Math.max(0, metrics.offsetLeft)}px`
  )
  root.style.setProperty(CSS_VARIABLES.SCALE, `${metrics.scale}`)
}

const attachViewportListeners = () => {
  if (typeof window === "undefined") return

  const update = () => setCSSVariables(getViewportMetrics())

  update()

  window.addEventListener("resize", update, { passive: true })
  window.addEventListener("orientationchange", update)
  window.addEventListener("scroll", update, { passive: true })

  if (window.visualViewport) {
    window.visualViewport.addEventListener("resize", update)
    window.visualViewport.addEventListener("scroll", update)
  }

  document.addEventListener("focusin", update)
}

/**
 * Ensure viewport variables are initialised once at app bootstrap.
 */
export const initViewportUnits = () => {
  if (initialized) return
  initialized = true
  attachViewportListeners()
}

